import { Bird, Brain, Shield } from 'lucide-react'
import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../core'
import { clearOtpState, requestOtpAction, selectOtpState } from '../slices'
import { LoginFormView } from './LoginFormView'
import { OtpVerificationForm } from './OtpVerificationForm'

type FormStep = 'email' | 'otp'

const useEmailFromQuery = () => {
  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)
  const email = searchParams.get('email')
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const isValid = email ? emailRegex.test(email) : false
  return { email: isValid ? email : null, isValid }
}

// Animated background with grid, particles, and aurora effect
const AnimatedBackground = () => {
  return (
    <div className="animated-bg">
      {/* Grid pattern */}
      <div className="grid-pattern" />

      {/* Aurora gradient waves */}
      <div className="aurora">
        <div className="aurora-wave wave-1" />
        <div className="aurora-wave wave-2" />
        <div className="aurora-wave wave-3" />
      </div>

      {/* Floating particles */}
      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${5 + ((i * 4.5) % 90)}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${6 + (i % 4) * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Scan line effect */}
      <div className="scan-line" />

      {/* Vignette overlay */}
      <div className="vignette" />
    </div>
  )
}

// Logo component
const BirdBrainLogo = () => (
  <div className="logo-container">
    <div className="logo-glow" />
    <div className="logo-icon">
      <Bird className="bird-icon" />
      <Brain className="brain-icon" />
    </div>
  </div>
)

export const LoginForm: React.FC = () => {
  const dispatch = useAppDispatch()
  const { isRequesting, otpSent, error } = useAppSelector(selectOtpState)

  const { email: queryEmail, isValid: isQueryEmailValid } = useEmailFromQuery()

  const [email, setEmail] = useState(queryEmail || '')
  const [validEmail, setValidEmail] = useState(isQueryEmailValid)
  const [currentStep, setCurrentStep] = useState<FormStep>(isQueryEmailValid ? 'otp' : 'email')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (isQueryEmailValid && queryEmail) {
      dispatch(requestOtpAction(queryEmail))
    }
  }, [dispatch, queryEmail, isQueryEmailValid])

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    setValidEmail(emailRegex.test(value))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validEmail) return

    try {
      await dispatch(requestOtpAction(email)).unwrap()
      setCurrentStep('otp')
    } catch (err) {
      console.error('OTP request failed:', err)
    }
  }

  const handleBack = () => {
    dispatch(clearOtpState())
    setCurrentStep('email')
  }

  // Show OTP form if we're on the OTP step or if OTP was sent
  const showOtpForm = currentStep === 'otp' || otpSent

  return (
    <div className={`login-container ${mounted ? 'mounted' : ''}`}>
      <AnimatedBackground />

      <div className="login-card">
        <BirdBrainLogo />

        <div className="login-content">
          <div className="title-section">
            <h1 className="main-title">
              <span className="title-bird">Bird</span>
              <span className="title-brain">Brain</span>
            </h1>
            <div className="subtitle">
              <Shield className="h-4 w-4" />
              <span>Admin Control Center</span>
            </div>
          </div>

          <div className="form-section">
            {showOtpForm ? (
              <OtpVerificationForm onBack={handleBack} />
            ) : (
              <LoginFormView
                email={email}
                validEmail={validEmail}
                isRequesting={isRequesting}
                error={error}
                onEmailChange={handleEmailChange}
                onSubmit={handleSubmit}
              />
            )}
          </div>
        </div>

        <div className="card-footer">
          <div className="security-badge">
            <div className="badge-dot" />
            <span>Your session is being audited</span>
          </div>
        </div>
      </div>

      <style>{`
        /* Forced-dark login splash: zinc neutrals are intentionally literal
           (app theme tokens flip in light mode). Brand/status colors reference
           mode-invariant tokens: --primary, --accent, --success, --destructive. */
        @import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,600,700&display=swap');
        @import url('https://cdn.jsdelivr.net/npm/geist@1.2.0/dist/fonts/geist-mono/style.min.css');

        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0A0A0F;
          padding: 1rem;
          position: relative;
          overflow: hidden;
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .login-container.mounted {
          opacity: 1;
        }

        /* Animated background */
        .animated-bg {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }

        /* Grid pattern */
        .grid-pattern {
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(hsl(var(--primary) / 0.03) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary) / 0.03) 1px, transparent 1px);
          background-size: 60px 60px;
          animation: gridMove 20s linear infinite;
        }

        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(60px, 60px); }
        }

        /* Aurora waves */
        .aurora {
          position: absolute;
          inset: 0;
          overflow: hidden;
          opacity: 0.6;
        }

        .aurora-wave {
          position: absolute;
          width: 200%;
          height: 200%;
          left: -50%;
          background: radial-gradient(ellipse at center, transparent 0%, transparent 50%, hsl(var(--primary) / 0.08) 70%, transparent 100%);
          animation: auroraFlow 15s ease-in-out infinite;
        }

        .wave-1 {
          top: -80%;
          animation-delay: 0s;
        }

        .wave-2 {
          top: -60%;
          background: radial-gradient(ellipse at center, transparent 0%, transparent 50%, hsl(var(--accent) / 0.05) 70%, transparent 100%);
          animation-delay: -5s;
          animation-duration: 18s;
        }

        .wave-3 {
          top: -70%;
          background: radial-gradient(ellipse at center, transparent 0%, transparent 50%, hsl(var(--primary) / 0.06) 70%, transparent 100%);
          animation-delay: -10s;
          animation-duration: 20s;
        }

        @keyframes auroraFlow {
          0%, 100% { 
            transform: translateX(-10%) rotate(-5deg) scale(1);
            opacity: 0.4;
          }
          25% { 
            transform: translateX(5%) rotate(0deg) scale(1.1);
            opacity: 0.6;
          }
          50% { 
            transform: translateX(10%) rotate(5deg) scale(1);
            opacity: 0.5;
          }
          75% { 
            transform: translateX(-5%) rotate(0deg) scale(1.05);
            opacity: 0.7;
          }
        }

        /* Floating particles */
        .particles {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: hsl(var(--primary));
          border-radius: 50%;
          bottom: -10px;
          opacity: 0;
          animation: particleRise 8s ease-out infinite;
        }

        .particle:nth-child(odd) {
          background: hsl(var(--accent));
          width: 2px;
          height: 2px;
        }

        @keyframes particleRise {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.3;
          }
          100% {
            transform: translateY(-100vh) translateX(20px);
            opacity: 0;
          }
        }

        /* Scan line effect */
        .scan-line {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, hsl(var(--primary) / 0.3), transparent);
          animation: scanMove 4s linear infinite;
          opacity: 0.5;
        }

        @keyframes scanMove {
          0% { top: -2px; }
          100% { top: 100%; }
        }

        /* Vignette overlay */
        .vignette {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at center, transparent 0%, transparent 50%, rgba(10, 10, 15, 0.8) 100%);
          pointer-events: none;
        }

        /* Main login card */
        .login-card {
          position: relative;
          width: 100%;
          max-width: 440px;
          background: #18181B;
          border: 1px solid #27272A;
          border-radius: 12px;
          padding: 1.5rem 1.25rem;
          box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(255, 255, 255, 0.03);
        }

        @media (min-width: 640px) {
          .login-card {
            padding: 2.5rem 2rem;
          }
        }

        /* Logo section */
        .logo-container {
          position: relative;
          width: 80px;
          height: 80px;
          margin: 0 auto 1.5rem;
        }

        .logo-glow {
          position: absolute;
          inset: -16px;
          background: radial-gradient(circle, hsl(var(--primary) / 0.25) 0%, transparent 70%);
          animation: glowPulse 4s ease-in-out infinite;
        }

        @keyframes glowPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }

        .logo-icon {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #27272A 0%, #18181B 100%);
          border-radius: 16px;
          border: 1px solid #3F3F46;
        }

        .bird-icon {
          position: absolute;
          width: 32px;
          height: 32px;
          color: hsl(var(--primary));
          filter: drop-shadow(0 0 8px hsl(var(--primary) / 0.5));
        }

        .brain-icon {
          position: absolute;
          width: 20px;
          height: 20px;
          color: hsl(var(--accent));
          transform: translate(10px, 10px);
          filter: drop-shadow(0 0 6px hsl(var(--accent) / 0.5));
        }

        /* Title section */
        .login-content {
          position: relative;
        }

        .title-section {
          text-align: center;
          margin-bottom: 1.5rem;
        }

        .main-title {
          font-family: 'Satoshi', system-ui, sans-serif;
          font-size: 2rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          margin-bottom: 0.5rem;
        }

        .title-bird {
          color: hsl(var(--primary));
        }

        .title-brain {
          color: hsl(var(--accent));
        }

        .subtitle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: #A1A1AA;
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
        }

        .subtitle svg {
          color: hsl(var(--primary));
        }

        /* Terminal header */
        /* Form section */
        .form-section {
          animation: fadeInUp 0.3s ease forwards;
          animation-delay: 0.2s;
          opacity: 0;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Card footer */
        .card-footer {
          margin-top: 1.5rem;
          padding-top: 1.25rem;
          border-top: 1px solid #27272A;
        }

        .security-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: #71717A;
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-size: 0.6rem;
        }

        .badge-dot {
          width: 6px;
          height: 6px;
          background: hsl(var(--success));
          border-radius: 50%;
          animation: statusPulse 2s infinite;
        }

        @keyframes statusPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  )
}
